/**
 * Fallbacks för webbläsar-API:er som bara finns i säkra kontexter.
 *
 * `crypto.subtle` och `navigator.clipboard` är begränsade till "secure
 * contexts" — https, eller http mot localhost/127.0.0.1. Lokalt körs appen på
 * http://localhost och allt fungerar, men utvecklings-VM:en nås på
 * http://diremo.local, som inte räknas som säker. Där är `crypto.subtle`
 * `undefined` och `navigator.clipboard` saknas.
 *
 * Se docker-compose/README.md, avsnittet om Proxmox-VM:en.
 */

import { sha256 } from 'js-sha256';

/**
 * SHA-256 av en sträng som hexadecimal text.
 *
 * Använder `crypto.subtle` när den finns och faller annars tillbaka på en
 * JS-implementation. De två ger byte-identiskt resultat (verifierat för ASCII,
 * UTF-8 och emoji), vilket är ett krav: hashen ingår i processvariabelnamnet
 * `favourite_<hash>` och ligger lagrad i databasen. En annan algoritm skulle
 * göra befintliga favoriter osynliga.
 */
export const sha256Hex = async (value: string): Promise<string> => {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const data = new TextEncoder().encode(value);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  return sha256(value);
};

/**
 * Kopierar text till urklipp.
 *
 * Returnerar `true` om det lyckades. I osäkra kontexter saknas
 * `navigator.clipboard`, och vi faller tillbaka på `document.execCommand`,
 * som är utfasat men fungerar utan säker kontext.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Kan nekas av behörighetsskäl — testa fallbacken nedan.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);

  try {
    textarea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};
